/**
 * Native alarm scheduling via Capacitor LocalNotifications.
 * When running inside the Capacitor Android shell, we mirror each
 * server-side schedule onto the OS so the device fires a notification
 * at the exact time — even if TimeFlash is closed or the phone is locked.
 */

import { Capacitor } from "@capacitor/core";
import { LocalNotifications } from "@capacitor/local-notifications";
import { buildReminderMessage } from "@/lib/format";

export const isNative = () => Capacitor?.isNativePlatform?.() === true;

const stableIdFromUuid = (uuid, suffix = 0) => {
  // LocalNotifications requires a numeric int32 id. Hash the uuid.
  let h = 0;
  const str = `${uuid}:${suffix}`;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  // Ensure positive and under 2^31
  return Math.abs(h) % 2147483000 + 1;
};

export const ensurePermissions = async () => {
  if (!isNative()) return "web";
  try {
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display === "granted") return "granted";
    const req = await LocalNotifications.requestPermissions();
    return req.display;
  } catch {
    return "unknown";
  }
};

/**
 * Mirror the full list of pending schedules onto the OS.
 * - Cancels every previously-mirrored TimeFlash notification first
 * - Schedules a notification for every reminder time (if any) + every main time
 * - Only schedules future times
 */
export const syncNativeAlarms = async (schedules) => {
  if (!isNative()) return { scheduled: 0, canceled: 0, skipped: "web" };

  await ensurePermissions();

  // 1) Cancel all previously-mirrored notifications
  try {
    const pending = await LocalNotifications.getPending();
    if (pending?.notifications?.length) {
      await LocalNotifications.cancel({
        notifications: pending.notifications.map((n) => ({ id: n.id })),
      });
    }
  } catch {}

  // 2) Build the schedule list
  const now = Date.now();
  const toSchedule = [];
  for (const s of schedules) {
    if (!s || !s.scheduled_at) continue;
    const mainMs = new Date(s.scheduled_at).getTime();

    // Reminder notification
    if (!s.reminder_triggered && s.reminder_minutes && s.reminder_minutes > 0) {
      const reminderMs = mainMs - s.reminder_minutes * 60 * 1000;
      if (reminderMs > now + 2000) {
        toSchedule.push({
          id: stableIdFromUuid(s.id, 1),
          title: "TimeFlash reminder",
          body: buildReminderMessage(s.message, s.reminder_minutes),
          schedule: { at: new Date(reminderMs), allowWhileIdle: true },
          sound: s.sound_enabled ? "beep.wav" : null,
          smallIcon: "ic_stat_zap",
          actionTypeId: "OPEN_FLASH",
          extra: { scheduleId: s.id, kind: "reminder" },
        });
      }
    }

    // Main notification
    if (!s.triggered && mainMs > now + 2000) {
      toSchedule.push({
        id: stableIdFromUuid(s.id, 0),
        title: "TimeFlash",
        body: s.message,
        schedule: { at: new Date(mainMs), allowWhileIdle: true },
        sound: s.sound_enabled ? "beep.wav" : null,
        smallIcon: "ic_stat_zap",
        actionTypeId: "OPEN_FLASH",
        extra: { scheduleId: s.id, kind: "main" },
      });
    }
  }

  if (toSchedule.length === 0) {
    return { scheduled: 0, canceled: 0 };
  }

  try {
    await LocalNotifications.schedule({ notifications: toSchedule });
    return { scheduled: toSchedule.length };
  } catch (err) {
    console.warn("Native schedule failed", err);
    return { scheduled: 0, error: String(err) };
  }
};

/**
 * Call once at app start: when the user taps a scheduled notification,
 * route them into the dashboard so the FlashOverlay can appear.
 */
export const registerNotificationTapHandler = (onTap) => {
  if (!isNative()) return () => {};
  let listener;
  LocalNotifications.addListener("localNotificationActionPerformed", (ev) => {
    try {
      const extra = ev?.notification?.extra;
      if (extra?.scheduleId) {
        onTap?.(extra);
      }
    } catch {}
  }).then((l) => {
    listener = l;
  });
  return () => {
    try {
      listener?.remove?.();
    } catch {}
  };
};
